import {
  convertBrowserLocaleToLanguageRegion,
  createAuthSyncRequest,
  createRealmWithNames,
  type Realm,
  Auth as STAuth,
  User as STUser,
} from "@storytell/cp-client";
import type { Named } from "@storytell/logger";
import type { Auth as FBAuth, User as FBUser } from "firebase/auth";

/** An account that hasn't signed up has no real email; the server stands `<user id>@guest.storytell.ai` in for one. */
export const isGuestEmail = (email: string | undefined): boolean => !!email?.endsWith("@guest.storytell.ai");

export const prepareSyncRequest = (
  user: Pick<FBUser, "email" | "displayName" | "photoURL">,
  deviceId: string,
  locale: string,
  userAgent: string
): STAuth.SyncRequest => {
  return createAuthSyncRequest({
    workflowVariant: STAuth.SyncWorkflowVariant.WEB_APP,
    isEmailVerified: false,
    email: user.email || "guest@guest.storytell.ai",
    displayName: user?.displayName || "Guest", // Todo: i18n default name
    photoUrl: user.photoURL || "https://storytell.ai/images/guest-avatar.png",
    userAgent,
    languageRegion: convertBrowserLocaleToLanguageRegion(locale),
    deviceId,
  });
};

export const createTokenRefresher = (auth: FBAuth, setToken: (token: string) => void, logger: Named) => {
  let schedule: NodeJS.Timeout | null = null;

  const refresh = async (force = true): Promise<void> => {
    if (!auth.currentUser) {
      logger.warn("No user to refresh token for");
      return;
    }
    logger.info("Refreshing token");
    const token = await auth.currentUser.getIdToken(force);
    setToken(token);
  };

  return {
    refresh,
    schedule: (seconds: number): void => {
      schedule = setInterval(() => {
        refresh().catch((error: unknown) =>
          logger.error("token_refresh_failed", "error refreshing auth token on schedule", error)
        );
      }, 1000 * seconds);
    },
    clear: () => {
      if (schedule) {
        clearInterval(schedule);
        schedule = null;
      }
    },
  };
};

export const emptyRealm = (): Realm.RealmWithNames => {
  return createRealmWithNames({
    tenantId: "",
    tenantName: "",
    organizationId: "",
    organizationName: "",
    workspaceId: "",
    workspaceName: "",
    projectId: undefined,
    projectName: undefined,
  });
};

// Checks if the user is currently authenticated and registered.
// This state indicates the user has completed both registration and email verification.
export const isLifecycleRegistered = (value: string | STUser.UserStatus) => value === STUser.UserStatus.REGISTERED;

// Checks if the user is currently in guest mode.
// Guest users have limited access and functionality compared to registered users.
export const isLifecycleGuest = (value: string | STUser.UserStatus) => value === STUser.UserStatus.GUEST;

// Checks if the authentication state is currently loading.
// This state is typically shown during initial load or during authentication processes.
export const isLifecycleLoading = (value: string | STUser.UserStatus) => value === "loading";

export const isLifecycleError = (value: string | STUser.UserStatus) => value === "error";
